<template>
  <el-card shadow="never" class="reveal">
    <template #header><span class="font-display font-bold text-profissa-text-main">Module de Sanction Manuelle</span></template>

    <el-form :inline="true" class="premium-form flex flex-wrap items-end gap-4" @submit.prevent="$emit('apply')">

      <!-- Recherche artisan -->
      <el-form-item label="Rechercher l'Artisan" class="flex-1 min-w-[240px]">
        <el-autocomplete
          v-model="searchQuery"
          :fetch-suggestions="fetchSuggestions"
          placeholder="Nom, téléphone ou N° CNIB…"
          clearable
          class="!w-full"
          value-key="display"
          @select="selectArtisan"
          @clear="clearSelection"
        >
          <template #default="{ item }">
            <div class="flex justify-between items-center py-1">
              <span class="font-bold text-sm text-profissa-text-main">{{ item.full_name || '—' }}</span>
              <span class="text-xs text-profissa-text-muted font-mono">
                {{ item.phone }}<template v-if="item.cnib_number"> · {{ item.cnib_number }}</template>
              </span>
            </div>
          </template>
          <template #suffix>
            <el-icon v-if="isSearching" class="is-loading"><Loading /></el-icon>
          </template>
        </el-autocomplete>
        <div v-if="selectedArtisan" class="mt-2 flex items-center gap-2">
          <el-tag type="success" size="small" class="font-bold uppercase tracking-wider">
            <el-icon class="mr-1"><Check /></el-icon>Sélectionné
          </el-tag>
          <span class="text-xs font-bold text-profissa-text-main">{{ selectedArtisan.full_name || selectedArtisan.phone }}</span>
        </div>
      </el-form-item>

      <!-- Sanction -->
      <el-form-item label="Mesure Administrative" class="w-48">
        <el-select :model-value="sanctionForm.state" class="!w-full" @change="update('state', $event)">
          <el-option value="active" label="Rétablir (Actif)" />
          <el-option value="under_review" label="Mettre sous surveillance" />
          <el-option value="suspended" label="Suspendre l'Accès" />
        </el-select>
      </el-form-item>

      <!-- Motif -->
      <el-form-item label="Motif de la Décision" class="flex-[2] min-w-[240px]">
        <el-input
          :model-value="sanctionForm.reason"
          placeholder="Justifiez la sanction (ex: non-respect des CGU)..."
          class="!w-full"
          @input="update('reason', $event)"
        />
      </el-form-item>

      <el-form-item>
        <el-button
          type="danger"
          :loading="isSearching || isApplying"
          :disabled="!canApply || !sanctionForm.artisanId"
          native-type="submit"
          class="font-black uppercase tracking-widest px-6"
          @click="$emit('apply')"
        >
          Appliquer
        </el-button>
      </el-form-item>

    </el-form>
  </el-card>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'
import { Loading, Check } from '@element-plus/icons-vue'
import { searchArtisans } from '../../services/adminApi'

const props = defineProps({
  sanctionForm:       { type: Object,  required: true },
  isApplying:         { type: Boolean, default: false },
  prefillArtisanId:   { type: String,  default: '' },
  prefillArtisanName: { type: String,  default: '' },
  canApply:           { type: Boolean, default: false },
})

const emit = defineEmits(['apply', 'update:sanctionForm'])

const update = (field, value) => emit('update:sanctionForm', { ...props.sanctionForm, [field]: value })

const searchQuery     = ref('')
const isSearching     = ref(false)
const selectedArtisan = ref(null)

const fetchSuggestions = async (query, cb) => {
  if (!props.canApply) { cb([]); return }
  if (!query.trim()) { cb([]); return }
  isSearching.value = true
  try {
    const results = await searchArtisans(query.trim())
    cb(results.map(a => ({ ...a, display: a.full_name || a.phone || '' })))
  } catch {
    cb([])
  } finally {
    isSearching.value = false
  }
}

const selectArtisan = (a) => {
  if (!props.canApply) return
  selectedArtisan.value = a
  searchQuery.value = a.full_name || a.phone || ''
  update('artisanId', a.id)
}

const clearSelection = () => {
  if (!props.canApply) return
  selectedArtisan.value = null
  update('artisanId', '')
}

watch(() => props.prefillArtisanId, (id) => {
  if (!id) return
  const name = props.prefillArtisanName || id.slice(0, 8).toUpperCase()
  selectedArtisan.value = { id, full_name: name, phone: '' }
  searchQuery.value = name
  update('artisanId', id)
})
</script>
